import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LayoutGrid, X, ArrowLeft, Maximize2, ChevronRight } from 'lucide-react';
import { FONT, MONO } from './ui';

const RETURN_KEY = 'vf_return_to';
const typing = (el) => el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));

// Side panel with the internal VocalFitness tools, opened inside the lecture (iframe) so the presentation is never left.
export const ToolsDock = ({ tools, L, t }) => {
  const internal = tools.filter((x) => x.url.startsWith('/'));
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [loaded, setLoaded] = useState([]);
  const [srcs, setSrcs] = useState({});

  useEffect(() => {
    const key = (e) => {
      if (typing(document.activeElement) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'r' || e.key === 'R') setOpen((o) => !o);
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, []);

  const show = (x, url) => {
    if (url) setSrcs((m) => ({ ...m, [x.id]: url }));
    setActive(x.id); setLoaded((l) => (l.includes(x.id) ? l : [...l, x.id]));
  };
  const showRef = useRef(show);
  showRef.current = show;
  useEffect(() => {
    const onOpen = (e) => {
      const x = internal.find((y) => y.id === e.detail?.id);
      if (x) { showRef.current(x, e.detail.url); setOpen(true); }
    };
    window.addEventListener('vf-open-tool', onOpen);
    return () => window.removeEventListener('vf-open-tool', onOpen);
  }, [internal]);
  const cur = internal.find((x) => x.id === active);
  const wide = open && cur;
  const frames = useRef({});
  // Background tools (closed dock / other tab) stop their audio + mic so the lecture stays smooth.
  useEffect(() => {
    Object.entries(frames.current).forEach(([id, f]) => {
      const w = f?.contentWindow;
      try { if (w) w.dispatchEvent(new w.CustomEvent('vf-audio', { detail: open && id === active })); } catch { /* not loaded */ }
    });
  }, [open, active]);
  return (
    <>
      <button onClick={() => setOpen((o) => !o)} className="fixed right-0 top-1/2 -translate-y-1/2 z-[205] flex flex-col items-center gap-2 rounded-l-2xl bg-[#060d24] hover:bg-blue-700 text-white px-2.5 py-4 shadow-2xl transition-colors" data-testid="tools-dock-toggle" aria-label={t.dockTitle}>
        <LayoutGrid className="w-5 h-5 text-amber-300" />
        <span className="text-[11px] font-bold tracking-widest [writing-mode:vertical-rl] rotate-180" style={MONO}>{t.dockTab}</span>
        <kbd className="text-[10px] px-1.5 rounded bg-white/15" style={MONO}>R</kbd>
      </button>
      {open && <div className="fixed inset-0 z-[206] bg-slate-950/40 backdrop-blur-[2px]" onClick={() => setOpen(false)} data-testid="tools-dock-backdrop" />}
      <aside className={`fixed top-0 right-0 bottom-0 z-[210] bg-[#060d24] text-white shadow-2xl flex flex-col transition-[transform,width] duration-300 ease-out ${open ? 'translate-x-0' : 'translate-x-full'} ${wide ? 'w-full lg:w-[92vw]' : 'w-full sm:w-[380px]'}`} data-testid="tools-dock-panel" aria-hidden={!open}>
        <header className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
          {cur && <button onClick={() => setActive(null)} className="p-2 rounded-full hover:bg-white/10" aria-label="back" data-testid="tools-dock-back"><ArrowLeft className="w-4 h-4" /></button>}
          <p className="flex-1 font-extrabold truncate" style={FONT}>{cur ? cur.name : t.dockTitle}</p>
          {cur && (
            <Link to={srcs[cur.id] || cur.url} onClick={() => sessionStorage.setItem(RETURN_KEY, JSON.stringify({ to: `${window.location.pathname}#resources`, label: t.backToResources }))}
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-semibold" data-testid="tools-dock-fullpage">
              <Maximize2 className="w-3.5 h-3.5" />{t.dockFull}
            </Link>
          )}
          <button onClick={() => setOpen(false)} className="p-2 rounded-full hover:bg-white/10" aria-label="close" data-testid="tools-dock-close"><X className="w-5 h-5" /></button>
        </header>
        {!cur && (
          <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="tools-dock-list">
            <p className="text-xs text-blue-100/60 mb-3">{t.dockHint}</p>
            {internal.map((x) => (
              <button key={x.id} onClick={() => show(x)} className="group w-full text-left rounded-2xl bg-white/5 hover:bg-blue-700 border border-white/10 p-4 transition-colors flex items-center gap-3" data-testid={`tools-dock-item-${x.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-bold">{x.name}</p>
                  <p className="mt-1 text-xs text-blue-100/70">{L(x.desc)}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-blue-300 group-hover:translate-x-0.5 transition-transform" />
              </button>
            ))}
          </div>
        )}
        <div className={`flex-1 relative bg-white ${cur ? '' : 'hidden'}`}>
          {internal.filter((x) => loaded.includes(x.id)).map((x) => (
            <iframe key={srcs[x.id] || x.url} ref={(f) => { frames.current[x.id] = f; }} src={srcs[x.id] || x.url} title={x.name} allow="microphone; autoplay; fullscreen"
              className={`absolute inset-0 w-full h-full border-0 ${x.id === active ? '' : 'invisible'}`} data-testid={`tools-dock-frame-${x.id}`} />
          ))}
        </div>
      </aside>
    </>
  );
};
